import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { DashboardBusiness, DashboardEmployee } from '~/hooks/apis/business-traveller/useBusinessDashboard';

import type { Societe } from '~/services/BusinessTraveller/BusinessTravellerService';

import { SocieteEmployeeFilters, hasSocieteEmployeeFilters, matchesSocieteEmployeeFilters } from './societeFilters';
import { getSilverGrantProgress } from './societeProgress';

dayjs.extend(utc);
dayjs.extend(timezone);

export const LE_STAFF_BUSINESS_ID = 'e3ba5516-d08a-44d4-9411-2d652674d2f2';
export type BusinessActivityFilter = 'all' | '12-months' | '6-months' | '90-days';
export type SocieteCohort =
  | 'enrolled'
  | 'not-enrolled'
  | 'one-away'
  | 'two-three-away'
  | 'activation-needed'
  | 'granted'
  | 'awaiting-grant';
export type TierGroup = 'unknown' | 'bronze' | 'silver' | 'gold-plus';
export const TIER_GROUPS: Array<{ key: TierGroup; label: string }> = [
  { key: 'unknown', label: 'No status / unknown' },
  { key: 'bronze', label: 'Bronze' },
  { key: 'silver', label: 'Silver' },
  { key: 'gold-plus', label: 'Gold+' },
];
export interface SocieteOverviewMetadata {
  settings: Societe | null;
  grants: { silverGrantDates: Record<string, string>; unknownEmployeeIds: Array<string> };
}

export function matchesBusinessActivity(business: DashboardBusiness, filter: BusinessActivityFilter, now = new Date()) {
  if (business.id === LE_STAFF_BUSINESS_ID) return false;
  if (filter === 'all') return true;
  if (!business.last_booking_at) return false;
  const today = dayjs(now).tz('Australia/Sydney').startOf('day');
  const cutoff =
    filter === '90-days' ? today.subtract(90, 'day') : today.subtract(filter === '6-months' ? 6 : 12, 'month');
  const booked = dayjs(business.last_booking_at);
  return booked.isValid() && booked.valueOf() >= cutoff.valueOf();
}

export function tierGroup(
  tier?: App.LuxLoyaltyTier | null,
  enrolment?: DashboardEmployee['societe_enrolment'],
): TierGroup {
  if (enrolment === 'unknown' || enrolment === 'not-enrolled' || !tier) return 'unknown';
  return tier === 'bronze' || tier === 'silver' ? tier : 'gold-plus';
}

export function matchesOverviewFilters(employee: DashboardEmployee, filters: SocieteEmployeeFilters) {
  if (!hasSocieteEmployeeFilters(filters)) return true;
  const tier =
    employee.societe_enrolment === 'not-enrolled'
      ? null
      : employee.societe_enrolment === 'enrolled'
        ? employee.tier
        : undefined;
  return matchesSocieteEmployeeFilters(tier, filters);
}

export function matchesSocieteCohort(
  employee: DashboardEmployee,
  cohort: SocieteCohort,
  metadata?: SocieteOverviewMetadata,
) {
  if (cohort === 'enrolled' || cohort === 'not-enrolled') return employee.societe_enrolment === cohort;
  if (cohort === 'granted') return !!metadata?.grants.silverGrantDates[employee.id];
  if (employee.tier && employee.tier !== 'bronze') return false;
  if (employee.societe_enrolment === 'unknown' || !employee.societe_enrolment || employee.qualified_orders == null)
    return false;
  const progress = getSilverGrantProgress(metadata?.settings, employee.qualified_orders);
  if (!progress) return false;
  if (cohort === 'one-away') return progress.remaining === 1;
  if (cohort === 'two-three-away') return progress.remaining >= 2 && progress.remaining <= 3;
  if (progress.remaining !== 0) return false;
  return cohort === 'activation-needed'
    ? employee.societe_enrolment === 'not-enrolled'
    : employee.societe_enrolment === 'enrolled';
}

export function summariseSociete(
  businesses: Array<DashboardBusiness>,
  filters: SocieteEmployeeFilters,
  metadata: Map<string, SocieteOverviewMetadata>,
) {
  const cohorts: Record<SocieteCohort, Set<string>> = {
    enrolled: new Set(),
    'not-enrolled': new Set(),
    'one-away': new Set(),
    'two-three-away': new Set(),
    'activation-needed': new Set(),
    granted: new Set(),
    'awaiting-grant': new Set(),
  };
  const groups: Record<TierGroup, Set<string>> = {
    unknown: new Set(),
    bronze: new Set(),
    silver: new Set(),
    'gold-plus': new Set(),
  };
  for (const business of businesses) {
    if (business.id === LE_STAFF_BUSINESS_ID) continue;
    for (const employee of business.employees) {
      if (!matchesOverviewFilters(employee, filters)) continue;
      // Membership is attached to the linked personal account; never merge unrelated unlinked employees.
      const identity = employee.credit_customer_id || employee.customer_id || `${business.id}:${employee.id}`;
      groups[tierGroup(employee.tier, employee.societe_enrolment)].add(identity);
      for (const cohort of Object.keys(cohorts) as Array<SocieteCohort>) {
        if (matchesSocieteCohort(employee, cohort, metadata.get(business.id))) cohorts[cohort].add(identity);
      }
    }
  }
  return { cohorts, groups };
}
